import { defineStore } from 'pinia'
import { ref } from 'vue'
import {
  ref as dbRef,
  onChildAdded,
  remove,
  push,
  query,
  orderByChild,
  startAt,
} from 'firebase/database'
import { syncQueuePath, type SyncAction, type SyncQueue } from '../../lib/Firebase/types'
import { db as firebaseDB } from '@/lib/Firebase'
import { useAllTaskStore } from './task'
import { useFloorplanStore } from '../data/Editor/floorplan'
import { useStorage } from '@vueuse/core'
import { trackFirebaseDownload } from '@/lib/Firebase/downloadTracker'

/**
 * 배치 처리를 위한 그룹화된 아이템 타입
 */
interface GroupedItem {
  firstAction: SyncAction
  lastAction: SyncAction
  lastData: Record<string, unknown>[]
  lastTimestamp: number
}

export const useSyncFirebaseStore = defineStore('sync-firebase', () => {
  const allTaskStore = useAllTaskStore()
  const floorplanStore = useFloorplanStore()

  const lastSyncedTimestamp = useStorage('last-sync-time', 0)

  const environment = import.meta.env.VITE_ENV
  const items = ref<SyncQueue[]>([])
  const currentCompanyId = ref<string | null>(null)
  const currentTaskId = ref<string | null>(null)

  /**
   * 구독 해제 함수
   */
  let unsubscribe: (() => void) | null = null

  /**
   * 배치 처리를 위한 버퍼 및 타이머
   */
  const BATCH_DELAY_MS = 100
  let batchBuffer: SyncQueue[] = []
  let batchTimer: ReturnType<typeof setTimeout> | null = null

  /**
   * 데이터에서 ID를 추출하는 헬퍼 함수
   * @param data 데이터 배열
   * @returns ID 문자열 또는 undefined
   */
  const extractId = (data: Record<string, unknown>[]): string | undefined => {
    if (!data || data.length === 0) return undefined
    const firstItem = data[0]
    return firstItem?.id?.toString()
  }

  /**
   * 두 데이터 배열을 fields_updated_at 기반으로 병합
   * @param oldData 기존 데이터 배열
   * @param newData 새로운 데이터 배열
   * @returns 병합된 데이터 배열
   */
  const mergeDataByFieldTimestamp = (
    oldData: Record<string, unknown>[],
    newData: Record<string, unknown>[],
  ): Record<string, unknown>[] => {
    if (!oldData || oldData.length === 0) return newData
    if (!newData || newData.length === 0) return oldData

    const oldItem = oldData[0]
    const newItem = newData[0]

    // fields_updated_at이 없으면 새 데이터로 덮어쓰기
    const oldFieldsUpdatedAt = oldItem.fields_updated_at as Record<string, string> | undefined
    const newFieldsUpdatedAt = newItem.fields_updated_at as Record<string, string> | undefined

    if (!newFieldsUpdatedAt) return newData
    if (!oldFieldsUpdatedAt) return newData

    // 필드별로 최신 타임스탬프 기준 병합
    const mergedItem: Record<string, unknown> = { ...oldItem }
    const mergedFieldsUpdatedAt: Record<string, string> = { ...oldFieldsUpdatedAt }

    for (const [field, newTimestamp] of Object.entries(newFieldsUpdatedAt)) {
      const oldTimestamp = oldFieldsUpdatedAt[field]

      // 기존 타임스탬프가 없거나 새 타임스탬프가 더 최신이면 업데이트
      if (!oldTimestamp || new Date(newTimestamp).getTime() > new Date(oldTimestamp).getTime()) {
        if (field in newItem) {
          mergedItem[field] = newItem[field]
        }
        mergedFieldsUpdatedAt[field] = newTimestamp
      }
    }

    mergedItem.fields_updated_at = mergedFieldsUpdatedAt
    return [mergedItem]
  }

  /**
   * 최적화된 액션 결정
   * @param firstAction 첫 번째 액션
   * @param lastAction 마지막 액션
   * @returns 최종 처리할 액션 (null이면 무시)
   */
  const determineOptimizedAction = (
    firstAction: SyncAction,
    lastAction: SyncAction,
  ): SyncAction | null => {
    // 마지막 액션이 DELETE면 DELETE 처리
    if (lastAction === 'DELETE') {
      // INSERT → DELETE: 생성 후 삭제 = 무시
      if (firstAction === 'INSERT') {
        return null
      }
      return 'DELETE'
    }

    // 마지막 액션이 INSERT/UPDATE면
    // 첫 액션이 INSERT면 INSERT, 아니면 UPDATE
    if (firstAction === 'INSERT' || firstAction === 'DELETE') {
      return 'INSERT'
    }
    return 'UPDATE'
  }

  /**
   * 배치 버퍼의 아이템들을 최적화하여 처리
   */
  const processBatch = async (): Promise<void> => {
    if (batchBuffer.length === 0) return

    const itemsToProcess = [...batchBuffer]
    batchBuffer = []

    itemsToProcess.sort((a, b) => Number(a.timestamp) - Number(b.timestamp))
    // 테이블별 → ID별로 그룹화
    const groupedByTableAndId = new Map<string, Map<string, GroupedItem>>()

    for (const item of itemsToProcess) {
      const { table, action, data, timestamp } = item
      const id = extractId(data)

      // ID가 없는 경우 개별 처리 (안전장치)
      if (!id) {
        await processItemDirect(item)
        continue
      }

      if (!groupedByTableAndId.has(table)) {
        groupedByTableAndId.set(table, new Map())
      }

      const tableGroup = groupedByTableAndId.get(table)!

      if (!tableGroup.has(id)) {
        // 첫 번째 아이템
        tableGroup.set(id, {
          firstAction: action,
          lastAction: action,
          lastData: data,
          lastTimestamp: Number(timestamp),
        })
      } else {
        // 이미 존재하는 ID - 업데이트 및 데이터 병합
        const existing = tableGroup.get(id)!
        existing.lastAction = action
        // fields_updated_at 기반으로 데이터 병합 (정보 손실 방지)
        existing.lastData = mergeDataByFieldTimestamp(existing.lastData, data)
        existing.lastTimestamp = Math.max(existing.lastTimestamp, Number(timestamp))
      }
    }

    // 그룹화된 아이템들 처리
    let maxTimestamp = lastSyncedTimestamp.value

    for (const [table, idMap] of groupedByTableAndId) {
      // 같은 테이블의 아이템들을 액션별로 그룹화
      const insertItems: Record<string, unknown>[] = []
      const updateItems: Record<string, unknown>[] = []
      const deleteItems: Record<string, unknown>[] = []

      for (const [_id, grouped] of idMap) {
        const optimizedAction = determineOptimizedAction(grouped.firstAction, grouped.lastAction)

        // timestamp는 항상 업데이트 (무시되는 시퀀스도 포함)
        maxTimestamp = Math.max(maxTimestamp, grouped.lastTimestamp)

        if (optimizedAction === null) {
          // INSERT → DELETE 시퀀스: 무시 (하지만 timestamp는 이미 업데이트됨)
          console.info(`[SyncStore] Skipping INSERT→DELETE sequence for ${table}`)
          continue
        }

        // 액션별로 분류
        switch (optimizedAction) {
          case 'INSERT':
            insertItems.push(...grouped.lastData)
            break
          case 'UPDATE':
            updateItems.push(...grouped.lastData)
            break
          case 'DELETE':
            deleteItems.push(...grouped.lastData)
            break
        }
      }

      // 테이블별 핸들러 호출 (배치로 한 번에)
      await dispatchToHandler(table, 'INSERT', insertItems)
      await dispatchToHandler(table, 'UPDATE', updateItems)
      await dispatchToHandler(table, 'DELETE', deleteItems)
    }

    // 커서 업데이트 (가장 큰 timestamp로)
    if (maxTimestamp > lastSyncedTimestamp.value) {
      lastSyncedTimestamp.value = maxTimestamp
    }
  }

  /**
   * 테이블별 핸들러로 디스패치
   * @param table 테이블명
   * @param action 액션
   * @param data 데이터 배열
   */
  const dispatchToHandler = async (
    table: string,
    action: SyncAction,
    data: Record<string, unknown>[],
  ): Promise<void> => {
    if (data.length === 0) return

    try {
      switch (table) {
        case 'TASKS':
          await allTaskStore.handleRealtimeUpdate(action, data)
          break
        case 'FACILITIES':
          await allTaskStore.handleRealtimeUpdateFacility(action, data)
          break
        case 'FLOORPLANS':
          await floorplanStore.handleRealtimeUpdateFloorplan(action, data)
          break
        default:
          console.warn(`[SyncStore] Unknown table type: ${table}`)
          break
      }
    } catch (error) {
      console.error(`[SyncStore] Failed to process ${table} ${action}:`, error)
    }
  }

  /**
   * 개별 아이템 직접 처리 (ID 추출 실패 시 폴백)
   * @param item 처리할 아이템
   */
  const processItemDirect = async (item: SyncQueue): Promise<void> => {
    try {
      const { table, action, data, timestamp } = item

      if (Number(timestamp) <= lastSyncedTimestamp.value) return

      await dispatchToHandler(table, action, data)
      lastSyncedTimestamp.value = Number(timestamp)
    } catch (error) {
      console.error(`[SyncStore] Failed to process item ${item.id}:`, error)
    }
  }

  /**
   * 아이템을 배치 버퍼에 추가
   * @param item 추가할 아이템
   */
  const addToBatch = (item: SyncQueue): void => {
    batchBuffer.push(item)

    // 기존 타이머가 있으면 클리어
    if (batchTimer) {
      clearTimeout(batchTimer)
    }

    // 새 타이머 설정
    batchTimer = setTimeout(() => {
      batchTimer = null
      processBatch()
    }, BATCH_DELAY_MS)
  }

  /**
   * 리스너 시작
   * @param companyId 회사 ID
   * @param taskId task ID (선택). 제공 시 task 하위 경로만 구독
   */
  const initSyncListener = async (companyId: string, taskId?: string): Promise<void> => {
    if (!companyId) return

    // 동일한 companyId + taskId로 이미 구독 중이면 무시
    if (
      currentCompanyId.value === companyId &&
      currentTaskId.value === (taskId ?? null) &&
      unsubscribe !== null
    ) {
      return
    }

    await stopSyncListener()
    currentCompanyId.value = companyId
    currentTaskId.value = taskId ?? null

    // 최초 설치 시 (lastSyncedTimestamp가 0) 현재 시간으로 초기화
    // 서버에서 이미 최신 데이터를 가져왔으므로 과거 Firebase 이벤트는 무시
    if (lastSyncedTimestamp.value === 0) {
      lastSyncedTimestamp.value = Date.now()
    }

    // 경로 구성: taskId가 있으면 task 하위 경로 (서버가 task별 하위 경로에 기록)
    const basePath = `${syncQueuePath}/${environment}/company-${companyId}`
    const path = taskId ? `${basePath}/task-${taskId}` : basePath
    const databaseRef = dbRef(firebaseDB, path)

    // 항상 timestamp 기반 쿼리만 사용 (새 이벤트만 수신)
    const queryRef = query(
      databaseRef,
      orderByChild('timestamp'),
      startAt(lastSyncedTimestamp.value + 1),
    )

    unsubscribe = onChildAdded(queryRef, snapshot => {
      const key = snapshot.key
      const val = snapshot.val()
      trackFirebaseDownload('sync_queue', val)

      if (key && val) {
        const item = { ...val, id: key } as SyncQueue & { id: string }

        // UI 디버깅용 배열 관리 (선택사항)
        if (items.value.length > 50) items.value.shift()
        items.value.push(item)

        // 배치 버퍼에 추가 (즉시 처리 대신)
        addToBatch(item)
      }
    })
  }

  /**
   * 리스너 중지
   */
  const stopSyncListener = async (): Promise<void> => {
    // 1. 배치 타이머 정리 및 남은 버퍼 즉시 처리
    if (batchTimer) {
      clearTimeout(batchTimer)
      batchTimer = null
    }
    // 남은 버퍼가 있으면 즉시 처리 (완료까지 대기)
    if (batchBuffer.length > 0) {
      await processBatch()
    }

    // 2. 저장해둔 구독 해제 함수가 있다면 실행
    if (unsubscribe) {
      unsubscribe() // 이 함수를 호출하면 리스너가 끊깁니다.
      unsubscribe = null
    }

    // 3. 상태 초기화
    if (currentCompanyId.value) {
      currentCompanyId.value = null
      currentTaskId.value = null
      items.value = []
    }
  }

  // 현재 구독 중인 경로 반환
  const getCurrentPath = (): string | null => {
    if (!currentCompanyId.value) return null
    const basePath = `${syncQueuePath}/${environment}/company-${currentCompanyId.value}`
    return currentTaskId.value ? `${basePath}/task-${currentTaskId.value}` : basePath
  }

  // 수동 삭제 (필요시 사용)
  const clearEvent = async (id: string): Promise<void> => {
    const path = getCurrentPath()
    if (!path) return
    try {
      const itemRef = dbRef(firebaseDB, `${path}/${id}`)
      await remove(itemRef)
      items.value = items.value.filter(i => i.id !== id)
    } catch (error) {
      console.error('[SyncStore] Failed to clear:', error)
    }
  }

  // 테스트용 추가
  const addItem = async (data: SyncQueue): Promise<void> => {
    const path = getCurrentPath()
    if (!path) return
    const listRef = dbRef(firebaseDB, path)
    await push(listRef, data)
  }

  return {
    items,
    initSyncListener,
    stopSyncListener,
    clearEvent,
    addItem,
    lastSyncedTimestamp,
  }
})
